/**
 * 订单待确认持久化（frontend-api-integration.md §5.8）
 * 按钱包保存未确认的下单请求；超时/5xx/身份失效时保留，明确拒单后清除。
 * 自动确认只 resolve / 查原单，不重发 POST /orders。
 */
import type { CreateOrderRequest } from '@/types/api';
import { getCurrentWalletAddress } from '@/common/services/request';

const STORAGE_PREFIX = 'axi_order_recovery_';

export interface PendingOrderRecovery {
  walletAddress: string;
  idempotencyKey: string;
  clientOrderId: string;
  body: CreateOrderRequest;
  createdAt: number;
  /** direct-v1 凭证（可选；旧 prepare 路径可无） */
  submissionMode?: string;
  submissionToken?: string;
  submissionExpiresAt?: string;
  /** UI 展示用元数据（非重发依据） */
  meta?: {
    direction?: string;
    amountHuman?: string;
    symbol?: string;
    type?: 0 | 1;
  };
}

function storageKey(wallet?: string): string | null {
  const addr = (wallet || getCurrentWalletAddress() || '').toLowerCase().trim();
  if (!addr) return null;
  return `${STORAGE_PREFIX}${addr}`;
}

export function loadPendingOrderRecovery(
  wallet?: string
): PendingOrderRecovery | null {
  const key = storageKey(wallet);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingOrderRecovery;
    if (
      !parsed?.idempotencyKey ||
      !parsed?.clientOrderId ||
      !parsed?.body ||
      !parsed?.walletAddress
    ) {
      return null;
    }
    // 切换钱包不可用另一钱包记录
    const current = (wallet || getCurrentWalletAddress() || '').toLowerCase();
    if (current && parsed.walletAddress.toLowerCase() !== current) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** 写入失败返回 false；调用方必须停止正式下单 */
export function savePendingOrderRecovery(record: PendingOrderRecovery): boolean {
  const key = storageKey(record.walletAddress);
  if (!key) return false;
  if (
    !record.idempotencyKey ||
    !record.clientOrderId ||
    !record.body ||
    !record.walletAddress
  ) {
    return false;
  }
  try {
    localStorage.setItem(key, JSON.stringify(record));
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as PendingOrderRecovery;
    return (
      parsed?.idempotencyKey === record.idempotencyKey &&
      parsed?.clientOrderId === record.clientOrderId
    );
  } catch {
    return false;
  }
}

export function clearPendingOrderRecovery(wallet?: string): void {
  const key = storageKey(wallet);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function hasPendingOrderRecovery(wallet?: string): boolean {
  return !!loadPendingOrderRecovery(wallet);
}

/** 明确业务拒绝时可清；未知结果必须保留 */
export function shouldClearOrderRecoveryOnError(err: unknown): boolean {
  const e = err as { statusCode?: number; code?: string; message?: string };
  const status = Number(e?.statusCode || 0);
  const code = String(e?.code || '').toUpperCase();
  const msg = String(e?.message || '').toUpperCase();
  const codeOrMsg = code || msg;

  // 未知结果：保留（身份/限流/超时/幂等冲突/5xx/网络）
  if (status === 401 || status === 403 || status === 408 || status === 429) {
    return false;
  }
  if (status === 409 && /IDEMPOTENCY|CLIENT.?ORDER|CONFLICT/.test(codeOrMsg)) {
    return false;
  }
  if (status >= 500) return false;
  if (/TIMEOUT|NETWORK|UNKNOWN|UNAUTHORIZED/.test(code)) return false;
  if (status === 0 && /TIMEOUT|NETWORK|UNKNOWN|UNAUTHORIZED|NET ERROR|FAILED TO FETCH/.test(codeOrMsg)) {
    return false;
  }

  // 明确拒单：清除（含会话不匹配，避免死循环弹恢复）
  // 须在 status===0 早退之前判断业务码，避免 400 体丢失 statusCode 时被当成「未确定」
  if (status === 400 || status === 422) return true;
  if (
    /INSUFFICIENT|INVALID|REJECT|EXPIRED|NOT_TRADABLE|MARKET|AMOUNT|STAKE|DIRECTION|PREDICTION|QUOTE_EXPIRED|BALANCE|INSURANCE_SESSION_MISMATCH|SESSION_MISMATCH|VALIDATION|ORDER_AMOUNT_OVERFLOW/.test(
      codeOrMsg
    )
  ) {
    return true;
  }
  // status 未知且无业务码：保留，走 resolve
  if (status === 0) return false;
  // 默认保留（偏安全）
  return false;
}

export type OrderRecoveryPromptResult =
  | 'none'
  | 'recovered'
  | 'cancelled'
  | 'failed';
